/**
 * HoroscopePdfDocument — @react-pdf/renderer Document component.
 *
 * Renders a full horoscope report as a downloadable PDF.
 * Sections: birth details header, rasi chart (text grid), planet table,
 * dasha summary, yogas, doshas, footer.
 *
 * @module HoroscopePdfDocument
 * @see PdfExportPanel (consumer)
 */
import React from 'react';
import { Document, Page, View, Text, StyleSheet, Font } from '@react-pdf/renderer';
import { colorSchemes } from './pdfColorSchemes';
import { getPdfLabels } from './pdfLabels';

/* ── Helpers ── */

const SIGN_NAMES = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces',
];

const PLANET_ABBREVS = {
  Sun: 'Su', Moon: 'Mo', Mars: 'Ma', Mercury: 'Me',
  Jupiter: 'Ju', Venus: 'Ve', Saturn: 'Sa', Rahu: 'Ra', Ketu: 'Ke',
};

/** Build a map: sign_index -> [planet abbreviations] from chart data. */
const buildHouseMap = (chart, labels) => {
  const map = {};
  for (let i = 0; i < 12; i++) map[i] = [];

  if (chart?.ascendant) {
    const idx = chart.ascendant.sign_index;
    if (idx >= 0 && idx < 12) {
      map[idx].push(labels.lang === 'ta' ? 'Asc' : 'Asc');
    }
  }

  (chart?.planets || []).forEach(p => {
    const idx = p.sign_index;
    if (idx >= 0 && idx < 12) {
      map[idx].push(PLANET_ABBREVS[p.planet] || p.planet.substring(0, 2));
    }
  });
  return map;
};

const formatDuration = (dp, labels) => {
  if (!dp) return '0';
  const parts = [];
  if (dp.years > 0) parts.push(`${dp.years}${labels.yrsUnit}`);
  if (dp.months > 0) parts.push(`${dp.months}${labels.monthsUnit}`);
  if (dp.days > 0) parts.push(`${dp.days}${labels.daysUnit}`);
  return parts.join(' ') || '0';
};

const formatTimestamp = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/* ── Styles factory — colour-scheme aware ── */

const createStyles = (scheme) => StyleSheet.create({
  page: {
    backgroundColor: scheme.background,
    padding: 30,
    fontFamily: 'Helvetica',
    fontSize: 9,
    color: scheme.text,
  },
  header: {
    backgroundColor: scheme.headerBg,
    padding: 12,
    borderRadius: 4,
    marginBottom: 12,
    textAlign: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: scheme.accentColor,
    marginBottom: 2,
  },
  headerSubtitle: {
    fontSize: 10,
    color: scheme.mutedText,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: scheme.accentColor,
    marginBottom: 6,
    marginTop: 10,
    borderBottomWidth: 1,
    borderBottomColor: scheme.tableBorder,
    paddingBottom: 3,
  },
  birthGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 10,
  },
  birthItem: {
    width: '33%',
    paddingVertical: 2,
    paddingRight: 8,
  },
  birthLabel: {
    fontSize: 7,
    color: scheme.mutedText,
    textTransform: 'uppercase',
  },
  birthValue: {
    fontSize: 9,
    fontWeight: 'bold',
    color: scheme.text,
  },
  // Rasi chart grid (4x4 diamond approximation as 4 rows)
  rasiGrid: {
    flexDirection: 'column',
    border: `1px solid ${scheme.tableBorder}`,
    marginBottom: 10,
  },
  rasiRow: {
    flexDirection: 'row',
  },
  rasiCell: {
    flex: 1,
    minHeight: 30,
    padding: 3,
    borderWidth: 0.5,
    borderColor: scheme.tableBorder,
    justifyContent: 'center',
    alignItems: 'center',
  },
  rasiCellLabel: {
    fontSize: 6,
    color: scheme.mutedText,
  },
  rasiCellPlanets: {
    fontSize: 8,
    fontWeight: 'bold',
    color: scheme.accentColor,
    textAlign: 'center',
  },
  // Planet table
  table: {
    marginBottom: 10,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: scheme.tableHeaderBg,
    paddingVertical: 4,
    paddingHorizontal: 4,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 3,
    paddingHorizontal: 4,
    borderBottomWidth: 0.5,
    borderBottomColor: scheme.tableBorder,
  },
  tableRowAlt: {
    backgroundColor: scheme.tableRowAlt,
  },
  thText: {
    fontSize: 7,
    fontWeight: 'bold',
    color: scheme.tableHeaderText,
  },
  tdText: {
    fontSize: 8,
    color: scheme.text,
  },
  colPlanet: { width: '13%' },
  colSign: { width: '13%' },
  colDegree: { width: '16%' },
  colNakshatra: { width: '16%' },
  colPada: { width: '8%' },
  colNavamsa: { width: '16%' },
  colRetro: { width: '8%' },
  // Dasha
  dashaCurrentBox: {
    backgroundColor: scheme.headerBg,
    padding: 8,
    borderRadius: 4,
    marginBottom: 8,
    borderLeftWidth: 3,
    borderLeftColor: scheme.accentColor,
  },
  dashaCurrentText: {
    fontSize: 9,
    fontWeight: 'bold',
    color: scheme.text,
  },
  // Yoga / Dosha cards
  card: {
    padding: 6,
    marginBottom: 4,
    borderRadius: 3,
    borderLeftWidth: 3,
  },
  cardName: {
    fontSize: 9,
    fontWeight: 'bold',
    color: scheme.text,
  },
  cardDesc: {
    fontSize: 7.5,
    color: scheme.mutedText,
    marginTop: 2,
  },
  cardBadge: {
    fontSize: 7,
    fontWeight: 'bold',
    paddingHorizontal: 4,
    paddingVertical: 1,
    borderRadius: 3,
  },
  cardRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  // Footer
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 30,
    right: 30,
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderTopWidth: 0.5,
    borderTopColor: scheme.tableBorder,
    paddingTop: 4,
  },
  footerText: {
    fontSize: 7,
    color: scheme.mutedText,
  },
});

/* ── South-Indian Rasi Chart Layout ──
 * The traditional south-Indian chart is a 4x4 grid with corners + edges.
 * House numbering starts from the ascendant sign.
 *
 * Grid positions (sign indices relative to ascendant):
 *   [Pisces]  [Aries]   [Taurus]   [Gemini]
 *   [Aquar.]  [corner]  [corner]   [Cancer]
 *   [Capri.]  [corner]  [corner]   [Leo]
 *   [Sagit.]  [Scorpio] [Libra]    [Virgo]
 *
 * We display a simplified 3x4 grid: top row (4 cells), middle (4 cells), bottom (4 cells).
 * Each cell shows sign abbreviation + planet abbreviations.
 */

const RASI_GRID_ORDER = [
  // Row 0: signs 11, 0, 1, 2  (from top-left: Pisces, Aries, Taurus, Gemini)
  [11, 0, 1, 2],
  // Row 1: signs 10, -, -, 3
  [10, -1, -1, 3],
  // Row 2: signs 9, -, -, 4
  [9, -1, -1, 4],
  // Row 3: signs 8, 7, 6, 5
  [8, 7, 6, 5],
];

const RasiChartSection = ({ chart, labels, styles }) => {
  const houseMap = buildHouseMap(chart, labels);
  const l = labels;
  const signLabels = SIGN_NAMES.map(s => l.signs[s] || s);

  return (
    <View>
      <Text style={styles.sectionTitle}>{l.rasiChartTitle}</Text>
      <View style={styles.rasiGrid}>
        {RASI_GRID_ORDER.map((row, ri) => (
          <View key={ri} style={styles.rasiRow}>
            {row.map((signIdx, ci) => {
              if (signIdx === -1) {
                // Empty center cells
                return (
                  <View key={`${ri}-${ci}`} style={styles.rasiCell}>
                    <Text style={styles.rasiCellPlanets}>{ri === 1 && ci === 1 ? (l.rasiChartTitle) : ''}</Text>
                  </View>
                );
              }
              const abbr = signLabels[signIdx]?.substring(0, 3) || SIGN_NAMES[signIdx]?.substring(0, 3);
              const planets = houseMap[signIdx] || [];
              return (
                <View key={`${ri}-${ci}`} style={styles.rasiCell}>
                  <Text style={styles.rasiCellLabel}>{abbr} ({signIdx + 1})</Text>
                  <Text style={styles.rasiCellPlanets}>{planets.join(' ')}</Text>
                </View>
              );
            })}
          </View>
        ))}
      </View>
    </View>
  );
};

/* ── Planet Table Section ── */

const PlanetTableSection = ({ chart, labels, styles }) => {
  const l = labels;
  const planets = [
    {
      planet: l.planets.Ascendant || 'Ascendant',
      sign: l.signs[chart.ascendant?.sign_name] || chart.ascendant?.sign_name,
      degree: `${chart.ascendant?.degree || 0}°${String(chart.ascendant?.minutes || 0).padStart(2, '0')}'`,
      nakshatra: chart.ascendant?.nakshatra || '',
      pada: chart.ascendant?.pada || '',
      navamsa: l.signs[chart.ascendant?.navamsa_name] || chart.ascendant?.navamsa_name || '',
      retrograde: '-',
    },
    ...(chart.planets || []).map(p => ({
      planet: l.planets[p.planet] || p.planet,
      sign: l.signs[p.sign_name] || p.sign_name,
      degree: `${p.degree}°${String(p.minutes).padStart(2, '0')}'`,
      nakshatra: p.nakshatra || '',
      pada: p.pada || '',
      navamsa: l.signs[p.navamsa_name] || p.navamsa_name || '',
      retrograde: p.is_retrograde ? 'R' : '-',
    })),
  ];

  return (
    <View style={styles.table}>
      <Text style={styles.sectionTitle}>{l.planetTableTitle}</Text>
      <View style={styles.tableHeader}>
        <Text style={[styles.thText, styles.colPlanet]}>{l.planet}</Text>
        <Text style={[styles.thText, styles.colSign]}>{l.sign}</Text>
        <Text style={[styles.thText, styles.colDegree]}>{l.degree}</Text>
        <Text style={[styles.thText, styles.colNakshatra]}>{l.nakshatra}</Text>
        <Text style={[styles.thText, styles.colPada]}>{l.pada}</Text>
        <Text style={[styles.thText, styles.colNavamsa]}>{l.navamsa}</Text>
        <Text style={[styles.thText, styles.colRetro]}>{l.retrograde}</Text>
      </View>
      {planets.map((p, i) => (
        <View key={i} style={[styles.tableRow, i % 2 === 1 ? styles.tableRowAlt : {}]}>
          <Text style={[styles.tdText, styles.colPlanet, { fontWeight: 'bold' }]}>{p.planet}</Text>
          <Text style={[styles.tdText, styles.colSign]}>{p.sign}</Text>
          <Text style={[styles.tdText, styles.colDegree]}>{p.degree}</Text>
          <Text style={[styles.tdText, styles.colNakshatra]}>{p.nakshatra}</Text>
          <Text style={[styles.tdText, styles.colPada]}>{p.pada}</Text>
          <Text style={[styles.tdText, styles.colNavamsa]}>{p.navamsa}</Text>
          <Text style={[styles.tdText, styles.colRetro]}>{p.retrograde}</Text>
        </View>
      ))}
    </View>
  );
};

/* ── Dasha Section ── */

const DashaSection = ({ dasha, labels, styles }) => {
  if (!dasha) return null;
  const l = labels;
  const cd = dasha.current_dasha;
  const currentText = cd
    ? `${l.planets[cd.mahadasha] || cd.mahadasha} ${l.mahadasha} > ${l.planets[cd.antardasha] || cd.antardasha} ${l.antardasha} > ${l.planets[cd.pratyantdasha] || cd.pratyantdasha} ${l.pratyantdasha}`
    : '';

  return (
    <View wrap={false}>
      <Text style={styles.sectionTitle}>{l.dashaTitle}</Text>

      {cd && (
        <View style={styles.dashaCurrentBox}>
          <Text style={{ fontSize: 8, color: styles.page?.color || '#333', marginBottom: 2 }}>{l.currentlyRunning}</Text>
          <Text style={styles.dashaCurrentText}>{currentText}</Text>
        </View>
      )}

      {/* Balance at Birth */}
      {dasha.dasha_balance && (
        <View style={{ flexDirection: 'row', marginBottom: 6, gap: 16 }}>
          <Text style={styles.tdText}>{l.birthDashaLord}: {l.planets[dasha.dasha_balance.lord] || dasha.dasha_balance.lord}</Text>
          <Text style={styles.tdText}>{l.balanceAtBirth}: {formatDuration(dasha.dasha_balance.remaining_parts, l)}</Text>
        </View>
      )}

      {/* Mahadasha Table */}
      <Text style={{ fontSize: 9, fontWeight: 'bold', marginBottom: 4 }}>{l.mahadashaPeriods}</Text>
      <View style={styles.tableHeader}>
        <Text style={[styles.thText, { width: '20%' }]}>{l.dashaLord}</Text>
        <Text style={[styles.thText, { width: '25%' }]}>{l.dashaDuration}</Text>
        <Text style={[styles.thText, { width: '27%' }]}>{l.dashaStart}</Text>
        <Text style={[styles.thText, { width: '28%' }]}>{l.dashaEnd}</Text>
      </View>
      {(dasha.mahadashas || []).map((md, i) => (
        <View key={i} style={[styles.tableRow, i % 2 === 1 ? styles.tableRowAlt : {}, md.is_current ? { borderLeftWidth: 2, borderLeftColor: styles.sectionTitle?.color || '#B8860B' } : {}]}>
          <Text style={[styles.tdText, { width: '20%', fontWeight: md.is_current ? 'bold' : 'normal' }]}>{l.planets[md.lord] || md.lord}</Text>
          <Text style={[styles.tdText, { width: '25%' }]}>{formatDuration(md.duration_parts, l)}</Text>
          <Text style={[styles.tdText, { width: '27%' }]}>{md.start_date}</Text>
          <Text style={[styles.tdText, { width: '28%' }]}>{md.end_date}</Text>
        </View>
      ))}
    </View>
  );
};

/* ── Yogas Section ── */

const YogasSection = ({ yogas, labels, styles, scheme }) => {
  if (!yogas || yogas.length === 0) return null;
  const l = labels;

  const strengthColor = (s) => {
    if (s === 'very strong' || s === 'strong') return scheme.yogaStrong;
    if (s === 'moderate') return scheme.yogaModerate;
    return scheme.yogaWeak;
  };

  return (
    <View>
      <Text style={styles.sectionTitle}>{l.yogasTitle} ({yogas.length})</Text>
      {yogas.slice(0, 10).map((y, i) => (
        <View key={i} style={[styles.card, { borderLeftColor: strengthColor(y.strength), backgroundColor: scheme.tableRowAlt }]}>
          <View style={styles.cardRow}>
            <Text style={styles.cardName}>{y.name}{y.sanskrit ? ` (${y.sanskrit})` : ''}</Text>
            <Text style={[styles.cardBadge, { color: strengthColor(y.strength) }]}>{y.strength}</Text>
          </View>
          <Text style={styles.cardDesc}>{y.description}</Text>
          {y.planets && y.planets.length > 0 && (
            <Text style={[styles.cardDesc, { marginTop: 1 }]}>{l.yogaPlanets}: {y.planets.map(p => l.planets[p] || p).join(', ')}</Text>
          )}
        </View>
      ))}
      {yogas.length > 10 && (
        <Text style={styles.cardDesc}>...and {yogas.length - 10} more yogas</Text>
      )}
    </View>
  );
};

/* ── Doshas Section ── */

const DoshasSection = ({ doshas, labels, styles, scheme }) => {
  if (!doshas || doshas.length === 0) return null;
  const l = labels;

  return (
    <View>
      <Text style={styles.sectionTitle}>{l.doshasTitle}</Text>
      {doshas.map((d, i) => (
        <View key={i} style={[styles.card, {
          borderLeftColor: d.present ? scheme.doshaPresent : scheme.doshaAbsent,
          backgroundColor: scheme.tableRowAlt,
        }]}>
          <View style={styles.cardRow}>
            <Text style={styles.cardName}>{d.name}</Text>
            <Text style={[styles.cardBadge, { color: d.present ? scheme.doshaPresent : scheme.doshaAbsent }]}>
              {d.present ? (d.severity ? d.severity.toUpperCase() : l.doshaPresent) : l.doshaNotPresent}
            </Text>
          </View>
          {d.description && <Text style={styles.cardDesc}>{d.description}</Text>}
          {d.present && d.remedies && d.remedies.length > 0 && (
            <Text style={[styles.cardDesc, { marginTop: 2 }]}>{l.doshaRemedies}: {d.remedies.join(', ')}</Text>
          )}
          {d.cancellations && d.cancellations.length > 0 && (
            <Text style={[styles.cardDesc, { marginTop: 2 }]}>{l.doshaCancellations}: {d.cancellations.join(', ')}</Text>
          )}
        </View>
      ))}
    </View>
  );
};

/* ── Main Document ── */

const HoroscopePdfDocument = ({ chartResult, lang, colorScheme }) => {
  const scheme = colorSchemes[colorScheme] || colorSchemes.light;
  const styles = createStyles(scheme);
  const labels = getPdfLabels(lang);

  const chart = chartResult?.chart;
  const dasha = chartResult?.dasha;
  const yogas = chartResult?.yogas;
  const doshas = chartResult?.doshas;
  const birthDetails = chartResult?.birth_details;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.headerTitle}>{labels.title}</Text>
          <Text style={styles.headerSubtitle}>{labels.subtitle}</Text>
        </View>

        {/* Birth Details */}
        <Text style={styles.sectionTitle}>{labels.birthDetailsTitle}</Text>
        <View style={styles.birthGrid}>
          <View style={styles.birthItem}>
            <Text style={styles.birthLabel}>{labels.name}</Text>
            <Text style={styles.birthValue}>{chartResult?.name || '-'}</Text>
          </View>
          <View style={styles.birthItem}>
            <Text style={styles.birthLabel}>{labels.gender}</Text>
            <Text style={styles.birthValue}>{chartResult?.gender || '-'}</Text>
          </View>
          <View style={styles.birthItem}>
            <Text style={styles.birthLabel}>{labels.dateOfBirth}</Text>
            <Text style={styles.birthValue}>{birthDetails?.date || '-'}</Text>
          </View>
          <View style={styles.birthItem}>
            <Text style={styles.birthLabel}>{labels.timeOfBirth}</Text>
            <Text style={styles.birthValue}>{birthDetails?.time || '-'}</Text>
          </View>
          <View style={styles.birthItem}>
            <Text style={styles.birthLabel}>{labels.placeOfBirth}</Text>
            <Text style={styles.birthValue}>{birthDetails?.place || '-'}</Text>
          </View>
          <View style={styles.birthItem}>
            <Text style={styles.birthLabel}>{labels.ayanamsa}</Text>
            <Text style={styles.birthValue}>{chart?.ayanamsa_mode || '-'} ({chart?.ayanamsa ? `${chart.ayanamsa}°` : ''})</Text>
          </View>
        </View>

        {/* Rasi Chart */}
        {chart && <RasiChartSection chart={chart} labels={labels} styles={styles} />}

        {/* Planet Table */}
        {chart && <PlanetTableSection chart={chart} labels={labels} styles={styles} />}

        {/* Dasha */}
        {dasha && <DashaSection dasha={dasha} labels={labels} styles={styles} />}

        {/* Yogas */}
        {yogas && <YogasSection yogas={yogas} labels={labels} styles={styles} scheme={scheme} />}

        {/* Doshas */}
        {doshas && <DoshasSection doshas={doshas} labels={labels} styles={styles} scheme={scheme} />}

        {/* Footer */}
        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>{labels.generatedBy}</Text>
          <Text style={styles.footerText}>{formatTimestamp()}</Text>
        </View>
      </Page>
    </Document>
  );
};

export default HoroscopePdfDocument;
